import React, { useState, useEffect, useCallback, useRef } from 'react';
import VideoCard from './VideoCard';
import type { VideoInterface } from '../../types/video.interface';
import { Loader } from '@beholos/holoscommon';

interface PaginatedVideoListProps {
  videos: VideoInterface[];
  onLoadMore: () => Promise<void>;
  hasMore: boolean;
  loading?: boolean;
  videoWrapperRef?: React.RefObject<HTMLDivElement | null>;
}

const PaginatedVideoList: React.FC<PaginatedVideoListProps> = ({
  videos,
  onLoadMore,
  hasMore,
  loading = false,
  videoWrapperRef
}) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || loading || !hasMore) return;

    setIsLoadingMore(true);
    try {
      await onLoadMore();
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, loading, hasMore, onLoadMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scrollContainer = videoWrapperRef?.current;

    if (!sentinel || !scrollContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMore && !loading) {
          handleLoadMore();
        }
      },
      {
        root: scrollContainer,
        rootMargin: '0px',
        threshold: 0.1
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.unobserve(sentinel);
      observer.disconnect();
    };
  }, [hasMore, isLoadingMore, loading, handleLoadMore, videoWrapperRef]);

  return (
    <div>
      <div className="grid grid-cols-4 gap-4">
        {videos.map((video) => (
          <VideoCard key={video._id} video={video} />
        ))}
      </div>

      {/* Sentinel element for intersection observer */}
      {hasMore && (
        <div
          ref={sentinelRef}
          className="h-4 w-full"
          aria-hidden="true"
        />
      )}

      {(isLoadingMore || loading) && (
        <div className="flex justify-center mt-8">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default PaginatedVideoList;